import { useContext, useEffect, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import { useHistory, useLocation, useParams } from 'react-router-dom';
import { Box, Button, Stack, Typography, useMediaQuery } from '@mui/material';
import { helpContext } from '../../context/HelpContext';
import { createTopicBreadcrumbs, createTopicsUrl } from "../../helpers/helpCenterUtils.js"
import { useHelpCenterRights } from "../../hooks/useHelpCenterRights";
import BackToTopicsButton from './BackToTopicsButton';
import HelpBreadcrumbs from "./HelpBreadcrumbs";
import TopicEditor from './TopicEditor';

export default function TopicPage() {
  const history = useHistory();
  const location = useLocation();
  const isMobile = useMediaQuery('(max-width:600px)');

  const { id } = useParams();

  const { canEdit } = useHelpCenterRights();

  const {
    allTopics, loading,
    createTopic, createTopicLoading,
    updateTopic, updateTopicLoading
  } = useContext(helpContext);

  const [currentTopic, setCurrentTopic] = useState(null);
  const [editState, setEditState] = useState();

  const breadcrumbData = useMemo(() => (
    currentTopic?.topicId
      ? createTopicBreadcrumbs(allTopics, currentTopic.topicId)
      : []
  ), [allTopics, currentTopic]);

  const draftTopic = useMemo(() => currentTopic || {
    id,
    title: '',
    description: '',
  }, [currentTopic, id]);

  const handleEditClick = () => {
    setEditState((prev) => ({
      ...prev,
      isCreateMode: false,
      isEditing: true,
    }));
  };

  const handleSave = async (topic) => {
    let topicInput = topic

    if (editState.isCreateMode) {
      topicInput = {
        ...topicInput,
        parentId: editState.parentTopicId,
      };
      delete topicInput.id;
    }

    let updated =
      editState.isCreateMode
        ? await createTopic(topicInput)
        : await updateTopic(topicInput);

    if (!updated) {
      console.error("** Failed to save topic");
      return;
    }

    leaveEditState();
  };

  const leaveEditState = () => {
    if (editState.returnToTopicsPage) {
      history.push(createTopicsUrl());
    }
    else {
      setEditState({
        isCreateMode: false,
        isEditing: false,
      });
    }
  };

  useEffect(() => {
    const found = allTopics.find(a => a.topicId == id);
    setCurrentTopic(found || null);

    const isEdit = Boolean(location.state?.edit);

    setEditState({
      isCreateMode: isEdit && (Number(id) === 0),
      isEditing: isEdit,
      parentTopicId: location.state?.parentTopicId,
      returnToTopicsPage: Boolean(location.state?.edit),
    });
  }, [allTopics, id]);

  if (loading || !editState) {
    return (
      <Box sx={{ p: 1 }}>
        <FormattedMessage id="helpCenter.topic.loading" />
      </Box>
    );
  }

  if (editState.isEditing && currentTopic || editState.isCreateMode) {
    return (
      <TopicEditor
        createMode={editState.isCreateMode}
        onCancel={leaveEditState}
        onSave={handleSave}
        topic={draftTopic}
        saving={createTopicLoading || updateTopicLoading}
      />
    );
  }

  return (
    <Box sx={{ maxWidth: '800px', mx: 'auto', py: 4, px: 2 }}>
      <BackToTopicsButton />
      <HelpBreadcrumbs breadcrumbData={breadcrumbData} />

      {!currentTopic ? (
        <>
          <Typography variant="h5" sx={{ mb: 2 }}>
            <FormattedMessage id="helpCenter.topic.missing" />
          </Typography>
          {false && (
            <Button variant="outlined" /*onClick={() => setIsEditing(true)}*/>
              <FormattedMessage id="helpCenter.topic.create" />
            </Button>
          )}
        </>
      ) : (
        <>
          <Stack
            direction={isMobile ? 'column' : 'row'}
            justifyContent="space-between"
            alignItems={isMobile ? 'flex-start' : 'center'}
            spacing={isMobile ? 1 : 0}
            mb={2}
          >
            <Typography variant="h4" sx={{ fontWeight: 'bold' }}>
              {currentTopic.title}
            </Typography>
            {canEdit &&
              <Button variant="outlined" onClick={handleEditClick}>
                <FormattedMessage id="btn.edit" />
              </Button>
            }
          </Stack>

          <Typography variant="subtitle1" sx={{ mb: 3, color: '#555' }}>
            {currentTopic.description}
          </Typography>
        </>
      )}
    </Box>
  );
}
